<!--
Copyright (C) 2020 Reciprocity, Inc - All Rights Reserved
Unauthorized use, copying, distribution, displaying, or public performance
of this file, via any medium, is strictly prohibited. All information
contained herein is proprietary and confidential and may not be shared
with any third party without the express written consent of Reciprocity, Inc.
-->

<template>
  <div class="datepicker-input">
    <i class="fa fa-calendar icon" />
    <b-form-input
      ref="input"
      :class="extraClass"
      v-bind="$attrs"
      :placeholder="placeholder"
      type="text"
      v-on="$listeners"
    />
    <i v-if="clearElementShown" class="fa fa-times clear-date" @click="clearValue" />
  </div>
</template>

<script>
import flatpickr from 'flatpickr';
import { BFormInput } from 'bootstrap-vue';
import { addDays } from 'date-fns';

export default {
  name: 'Datepicker',
  components: {
    BFormInput,
  },
  props: {
    value: {
      type: null,
      default: null,
    },
    showClearElement: {
      type: Boolean,
      default: false,
    },
    enableTime: {
      type: Boolean,
      default: false,
    },
    initialAltFormat: {
      type: String,
      default: null,
    },
    initialDateFormat: {
      type: String,
      default: null,
    },
    altInput: {
      type: Boolean,
      default: false,
    },
    extraClass: {
      type: String,
      default: null,
    },
    placeholder: {
      type: String,
      default: '',
    },
    minDate: {
      type: String,
      default: '',
    },
    name: {
      type: String,
      default: '',
    },
  },
  computed: {
    defaultFormat() {
      return `m/d/Y${this.enableTime ? ' H:i' : ''}`;
    },
    dateFormat() {
      return this.initialDateFormat || this.defaultFormat;
    },
    altFormat() {
      return this.initialAltFormat || this.defaultFormat;
    },
    clearElementShown() {
      return this.showClearElement && this.value && this.value.length;
    },
  },
  watch: {
    async value(newVal) {
      this.flatpickrInstance.setDate(newVal);
    },
  },
  mounted() {
    this.flatpickrInstance = flatpickr(this.$refs.input.$el, {
      altInput: this.altInput,
      altFormat: this.altFormat,
      allowInput: true,
      dateFormat: this.dateFormat,
      defaultDate: this.fixDateTimeZone(),
      enableTime: this.enableTime,
      minDate: this.minDate,
      onChange: (selectedDates, dateString) => {
        this.$emit('touch');
        this.$emit('input', dateString);
      },
    });

    this.flatpickrInstance._input.addEventListener('blur', this.forceSetDate);
  },
  updated() {
    this.flatpickrInstance.setDate(this.value);
  },
  beforeDestroy() {
    this.flatpickrInstance._input.removeEventListener('blur', this.forceSetDate);
    this.flatpickrInstance.destroy();
  },
  methods: {
    fixDateTimeZone() {
      if (!this.value) {
        return this.value;
      }

      let date = new Date(this.value);
      // Check if we currently have negative timezone offset and add one day (only when dealing with dates)
      if (!this.enableTime && date.getTimezoneOffset() > 0) {
        // Javascript does not really have dates, it always represents them as date/time.
        // Depending on the timezone, it can display "wrong" date. Thus this fix.
        // Here is what can happen:
        //  - date is saved to database as 2010-10-10, it is read and forwarded here
        //  - when time is not provided to new Date(..), it takes 00:00.00 as default. E.g. 2010-10-10T00:00.00
        //  - BUT javascript also overlays timezone info on top of it, since Date is handled as a timestamp
        //  - user opens the app in negative timezone (taking daylight savings into account) -> UTC-X
        //  - javascript uses the negative timezone offset to re-calculate the date, and pushes it to the previous day
        //  - suddenly the date is shown as yesterday -> 2010-10-09
        // Note: it's not just timezone, it's also daylight savings time, but what Date object is aware of is what matters
        date = addDays(date, 1);
      }
      return date;
    },
    clearValue() {
      this.$emit('input', '');
    },
    forceSetDate() {
      // We do this to save the changed value to this.value
      this.flatpickrInstance.setDate(this.flatpickrInstance._input.value, true, this.altFormat);
    },
  },
};
</script>
